/**
 * premium-enhancements.js - Module de fonctionnalités premium
 * Gère : PREMIUM_MODE, PERF_MODE, Audio, Animations, Monitoring FPS
 */

(function() {
  'use strict';
  
  // ===============================================
  // CONFIGURATION GLOBALE
  // ===============================================
  
  window.PREMIUM_MODE = true;  // Flag global pour activer/désactiver
  window.PERF_MODE = 'auto';   // 'auto' | 'high' | 'low'
  
  const PERF_CONFIG = {
    fpsThreshold: 45,        // FPS minimum avant fallback
    fpsRestore: 55,          // FPS pour revenir en high (hystérésis)
    checkDuration: 3000,     // Durée de vérification (ms) - augmentée
    sampleRate: 200,         // Fréquence d'échantillonnage (ms) - réduite
    cooldown: 5000,          // Cooldown entre switches (ms)
  };
  
  // ===============================================
  // PERFORMANCE MONITOR
  // ===============================================
  
  class PerformanceMonitor {
    constructor() {
      this.samples = [];
      this.isMonitoring = false;
      this.currentMode = 'high';
      this.lastFrameTime = performance.now();
      this.lastSwitch = 0; // Timestamp du dernier switch
    }
    
    start() {
      if (this.isMonitoring) return;
      this.isMonitoring = true;
      this.lastFrameTime = performance.now();
      this.monitor();
    }
    
    stop() {
      this.isMonitoring = false;
    }
    
    monitor() {
      if (!this.isMonitoring) return;
      
      const now = performance.now();
      const delta = now - this.lastFrameTime;
      
      // Ignorer les deltas aberrants (tab en background, etc.)
      if (delta > 0 && delta < 1000) {
        const fps = 1000 / delta;
        this.samples.push(fps);
      }
      
      // Garder seulement les derniers samples
      const maxSamples = PERF_CONFIG.checkDuration / PERF_CONFIG.sampleRate;
      if (this.samples.length > maxSamples) {
        this.samples.shift();
      }
      
      // Vérifier si on doit switcher (avec cooldown)
      const timeSinceSwitch = now - this.lastSwitch;
      if (this.samples.length >= maxSamples && timeSinceSwitch > PERF_CONFIG.cooldown) {
        const avgFps = this.samples.reduce((a, b) => a + b) / this.samples.length;
        
        // Switch vers low-perf
        if (avgFps < PERF_CONFIG.fpsThreshold && this.currentMode === 'high') {
          this.switchToLowPerf();
          this.lastSwitch = now;
        } 
        // Switch vers high-perf (avec hystérésis)
        else if (avgFps >= PERF_CONFIG.fpsRestore && this.currentMode === 'low') {
          this.switchToHighPerf();
          this.lastSwitch = now;
        }
      }
      
      this.lastFrameTime = now;
      requestAnimationFrame(() => this.monitor());
    }
    
    switchToLowPerf() {
      console.warn('[Perf] Switching to LOW performance mode');
      this.currentMode = 'low';
      document.body.classList.add('low-perf');
      
      // Désactiver certains effets
      if (window.premiumUI) {
        window.premiumUI.reduceEffects();
      }
      
      // Toast notification
      if (window.showToast) {
        window.showToast('Mode performance réduit activé');
      }
    }
    
    switchToHighPerf() {
      console.log('[Perf] Switching to HIGH performance mode');
      this.currentMode = 'high';
      document.body.classList.remove('low-perf');
      
      if (window.premiumUI) {
        window.premiumUI.restoreEffects();
      }
    }
    
    getAverageFPS() {
      if (this.samples.length === 0) return 60;
      return Math.round(this.samples.reduce((a, b) => a + b) / this.samples.length);
    }
  }
  
  // ===============================================
  // PREMIUM UI MANAGER
  // ===============================================
  
  class PremiumUI {
    constructor() {
      this.audioManager = null;
      this.preloadManager = null;
      this.perfMonitor = null;
      this.effectsEnabled = true;
      this.loaderElement = null;
    }
    
    async init() {
      console.log('[Premium] Initializing...');
      
      // Appliquer le mode premium au body
      if (window.PREMIUM_MODE) {
        document.body.classList.add('premium-mode');
      }
      
      // Créer le loader
      this.createLoader();
      
      // Initialiser l'audio
      if (window.AudioManager) {
        this.audioManager = new AudioManager();
        await this.audioManager.init();
        window.audioManager = this.audioManager;
      }
      
      // Initialiser le preloader
      if (window.PreloadManager) {
        this.preloadManager = new PreloadManager();
        this.setupPreload();
      }
      
      // Performance Monitor désactivé par défaut (trop instable)
      // Pour activer : window.PREMIUM_CONFIG.perfMode = 'auto' puis recharger
      // OU utiliser window.PREMIUM_PRESETS.experimental()
      if (window.PERF_MODE === 'auto' && window.PREMIUM_CONFIG?.enablePerfMonitor === true) {
        this.perfMonitor = new PerformanceMonitor();
        setTimeout(() => {
          if (this.perfMonitor) {
            console.log('[Perf] Starting performance monitoring...');
            this.perfMonitor.start();
          }
        }, 5000); // Attendre 5s pour stabilisation
      } else if (window.PERF_MODE === 'low') {
        document.body.classList.add('low-perf');
      }
      
      // Setup event listeners
      this.setupEventListeners();
      
      // Setup mobile optimizations
      this.setupMobileOptimizations();
      
      console.log('[Premium] Initialized');
    }
    
    createLoader() {
      this.loaderElement = document.createElement('div');
      this.loaderElement.className = 'loader-screen';
      this.loaderElement.innerHTML = `
        <div class="loader-logo">Bulle</div>
        <div class="loader-progress">
          <div class="loader-progress-bar" id="loaderProgressBar"></div>
        </div>
        <div class="loader-text" id="loaderText">Chargement...</div>
      `;
      document.body.appendChild(this.loaderElement);
    }
    
    setupPreload() {
      if (!this.preloadManager) return;
      
      console.log('[Premium] Setting up preloader...');
      
      // Assets critiques
      this.preloadManager.addAssets('critical', [
        '/assets/cards/placeholder.jpg',
        '/assets/cards/placeholder.png'
      ]);
      
      // Sons
      const sounds = ['tap', 'whoosh', 'flip', 'sparkle', 'score', 'win', 'draw', 'play'];
      sounds.forEach(sound => {
        this.preloadManager.addAssets('critical', `/audio/${sound}.mp3`);
      });
      
      console.log('[Premium] Critical assets:', this.preloadManager.assets.critical.length);
      
      // Cartes (on charge les 10 premières visibles dans le data)
      try {
        if (window.CARDS && window.CARDS.length > 0) {
          const firstCards = window.CARDS.slice(0, 10);
          firstCards.forEach(card => {
            if (card.illustration) {
              this.preloadManager.addAssets('cards', card.illustration);
            }
          });
          console.log('[Premium] Card assets:', this.preloadManager.assets.cards.length);
        }
      } catch (e) {
        console.warn('[Premium] Could not preload cards:', e);
      }
      
      // Callbacks
      this.preloadManager.on('progress', (progress, loaded, total) => {
        const bar = document.getElementById('loaderProgressBar');
        const text = document.getElementById('loaderText');
        if (bar) bar.style.width = `${progress}%`;
        if (text) text.textContent = `Chargement... ${progress}%`;
        console.log(`[Preload] ${loaded}/${total} (${progress}%)`);
      });
      
      this.preloadManager.on('complete', () => {
        console.log('[Preload] Complete! Hiding loader in 800ms...');
        setTimeout(() => this.hideLoader(), 800);
      });
      
      this.preloadManager.on('error', (error) => {
        console.error('[Preload] Error:', error);
        setTimeout(() => this.hideLoader(), 500);
      });
      
      // Démarrer
      console.log('[Premium] Starting preload...');
      this.preloadManager.start();
    }
    
    hideLoader() {
      if (this.loaderElement) {
        console.log('[Premium] Hiding loader...');
        this.loaderElement.classList.add('hidden');
        setTimeout(() => {
          if (this.loaderElement) {
            this.loaderElement.remove();
            this.loaderElement = null;
            console.log('[Premium] Loader removed');
          }
        }, 500);
      }
    }
    
    setupEventListeners() {
      // Unlock audio au premier tap
      const unlockAudio = () => {
        if (this.audioManager) {
          this.audioManager.unlock();
        }
        document.removeEventListener('touchstart', unlockAudio);
        document.removeEventListener('click', unlockAudio);
      };
      
      document.addEventListener('touchstart', unlockAudio, { once: true });
      document.addEventListener('click', unlockAudio, { once: true });
      
      // Sons sur les boutons
      this.addButtonSounds();
      
      // Animations sur les cartes
      this.addCardAnimations();
    }
    
    addButtonSounds() {
      // Intercepter tous les clics sur les boutons
      document.addEventListener('click', (e) => {
        const button = e.target.closest('button');
        if (button && this.audioManager && !button.disabled) {
          this.audioManager.play('tap', { volume: 0.7 });
        }
      });
    }
    
    addCardAnimations() {
      // Observer pour détecter les nouvelles cartes ajoutées
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === 1 && node.classList.contains('card-item')) {
              this.animateCardEntry(node);
            }
          });
        });
      });
      
      // Observer le conteneur de main
      const handContainer = document.getElementById('handCardsContainer');
      if (handContainer) {
        observer.observe(handContainer, { childList: true });
      }
    }
    
    animateCardEntry(cardElement) {
      // Jouer le son de draw
      if (this.audioManager) {
        this.audioManager.play('draw', { volume: 0.6 });
      }
      
      // Ajouter l'animation
      cardElement.classList.add('card-draw-animation');
      setTimeout(() => {
        cardElement.classList.remove('card-draw-animation');
      }, 420);
      
      // Créer des particules (sparkles)
      this.createSparkles(cardElement, 3);
    }
    
    createSparkles(element, count = 3) {
      if (!this.effectsEnabled) return;
      
      const rect = element.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;
      
      for (let i = 0; i < count; i++) {
        const sparkle = document.createElement('div');
        sparkle.className = 'score-sparkle';
        
        const angle = (Math.PI * 2 * i) / count;
        const distance = 30 + Math.random() * 20;
        const tx = Math.cos(angle) * distance;
        const ty = Math.sin(angle) * distance;
        
        sparkle.style.cssText = `
          left: ${centerX}px;
          top: ${centerY}px;
          --tx: ${tx}px;
          --ty: ${ty}px;
        `;
        
        document.body.appendChild(sparkle);
        
        setTimeout(() => sparkle.remove(), 420);
      }
    }
    
    setupMobileOptimizations() {
      // Désactiver le zoom sur double-tap (SAUF dans les panels scrollables)
      let lastTouchEnd = 0;
      document.addEventListener('touchend', (e) => {
        // Ne pas empêcher le double-tap dans les panels/modals scrollables
        const isInScrollable = e.target.closest('.panelBody, .zone-modal-body, .card-modal-body, .handCards, .handDock, textarea, input');
        if (isInScrollable) return;
        
        const now = Date.now();
        if (now - lastTouchEnd <= 300) {
          e.preventDefault();
        }
        lastTouchEnd = now;
      }, { passive: false });
      
      // Empêcher le pull-to-refresh UNIQUEMENT sur le body principal, pas dans les panels
      let touchStartY = 0;
      let touchStartElement = null;
      
      document.addEventListener('touchstart', (e) => {
        touchStartY = e.touches[0].clientY;
        touchStartElement = e.target;
      }, { passive: true });
      
      document.addEventListener('touchmove', (e) => {
        // Ne pas bloquer le scroll dans les conteneurs scrollables
        const isInScrollable = e.target.closest('.panelBody, .zone-modal-body, .card-modal-body, .handCards, .handDock');
        if (isInScrollable) return;
        
        const touchY = e.touches[0].clientY;
        const touchDiff = touchY - touchStartY;
        
        // Empêcher pull-to-refresh seulement si on est en haut de page ET pas dans un scrollable
        if (touchDiff > 0 && window.scrollY === 0) {
          e.preventDefault();
        }
      }, { passive: false });
      
      // Désactiver le tilt 3D sur mobiles faibles
      if (this.isMobileDevice() && this.isLowEndDevice()) {
        document.body.classList.add('no-tilt');
      }
    }
    
    isMobileDevice() {
      return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    }
    
    isLowEndDevice() {
      // Heuristique simple pour détecter les appareils faibles
      return navigator.hardwareConcurrency <= 4 || 
             /Android [1-6]\./.test(navigator.userAgent);
    }
    
    reduceEffects() {
      this.effectsEnabled = false;
      console.log('[Premium] Effects reduced for performance');
    }
    
    restoreEffects() {
      this.effectsEnabled = true;
      console.log('[Premium] Effects restored');
    }
    
    // API publique pour déclencher des animations
    playCardAnimation(element, type = 'play') {
      if (!element) return;
      
      element.classList.add(`card-${type}-animation`);
      
      if (this.audioManager) {
        this.audioManager.play(type, { volume: 0.8 });
      }
      
      setTimeout(() => {
        element.classList.remove(`card-${type}-animation`);
      }, type === 'flip' ? 420 : 260);
    }
    
    showScoreEffect(x, y, points) {
      if (this.audioManager) {
        this.audioManager.play('score', { volume: 0.9 });
      }
      
      // Créer des sparkles à la position du score
      for (let i = 0; i < 5; i++) {
        const sparkle = document.createElement('div');
        sparkle.className = 'score-sparkle';
        
        const angle = (Math.PI * 2 * i) / 5;
        const distance = 40 + Math.random() * 30;
        const tx = Math.cos(angle) * distance;
        const ty = Math.sin(angle) * distance;
        
        sparkle.style.cssText = `
          left: ${x}px;
          top: ${y}px;
          --tx: ${tx}px;
          --ty: ${ty}px;
        `;
        
        document.body.appendChild(sparkle);
        setTimeout(() => sparkle.remove(), 420);
      }
    }
  }
  
  // ===============================================
  // INITIALISATION GLOBALE
  // ===============================================
  
  window.premiumUI = new PremiumUI();
  
  // Initialiser dès que le DOM est prêt
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      window.premiumUI.init();
    });
  } else {
    window.premiumUI.init();
  }
  
  // API globale pour les autres scripts
  window.playSound = (soundName, options) => {
    if (window.audioManager) {
      window.audioManager.play(soundName, options);
    }
  };
  
  window.animateCard = (element, type) => {
    if (window.premiumUI) {
      window.premiumUI.playCardAnimation(element, type);
    }
  };
  
  window.showScoreEffect = (x, y, points) => {
    if (window.premiumUI) {
      window.premiumUI.showScoreEffect(x, y, points);
    }
  };
  
  console.log('[Premium] Module loaded');

  // --- Viewport vars for mobile (fix 100vh + modal scroll) ---
  function setViewportVars(){
    try{
      const h = window.innerHeight || document.documentElement.clientHeight || 0;
      document.documentElement.style.setProperty('--appH', h + 'px');
      // Estimate available height for modal: innerHeight - topbar - margins
      const topbarH = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--topbarH')) || 64;
      const available = Math.max(320, h - topbarH - 28); // keep usable min
      document.documentElement.style.setProperty('--modalMaxH', available + 'px');
    }catch(e){}
  }
  setViewportVars();
  window.addEventListener('resize', setViewportVars, {passive:true});
  window.addEventListener('orientationchange', setViewportVars, {passive:true});
})();
